import React from 'react';
import {
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Button,
  List,
  Divider,
  Box,
} from '@shopify/polaris';

export interface PlanFeature {
  name: string;
  included: boolean;
}

export interface PricingPlan {
  id: number;
  key: string;
  name: string;
  description?: string;
  priceMonthly: number;
  priceYearly?: number;
  priceMonthlyFormatted: string;
  priceYearlyFormatted?: string;
  orderLimit: number | null;
  orderLimitFormatted: string;
  features: Record<string, boolean>;
}

interface PricingCardProps {
  plan: PricingPlan;
  currentPlanKey?: string;
  billingCycle: 'monthly' | 'yearly';
  onSelect: (planId: number) => void;
  isLoading?: boolean;
}

const FEATURE_LABELS: Record<string, string> = {
  basic_sync: 'Order sync to Tally',
  email_support: 'Email support',
  priority_support: 'Priority support',
  phone_support: 'Phone support',
  dedicated_support: 'Dedicated account manager',
  scheduled_sync: 'Scheduled sync',
  custom_field_mapping: 'Custom field mapping',
  multi_store: 'Multi-store support',
  sla: 'SLA guarantee',
};

export const PricingCard: React.FC<PricingCardProps> = ({
  plan,
  currentPlanKey,
  billingCycle,
  onSelect,
  isLoading,
}) => {
  const isCurrent = plan.key === currentPlanKey;
  const price = billingCycle === 'yearly' && plan.priceYearly
    ? plan.priceYearly
    : plan.priceMonthly;
  const priceFormatted = billingCycle === 'yearly' && plan.priceYearlyFormatted
    ? plan.priceYearlyFormatted
    : plan.priceMonthlyFormatted;

  const monthlyEquivalent = billingCycle === 'yearly' && plan.priceYearly
    ? `${(plan.priceYearly / 12 / 100).toFixed(0)}`
    : null;

  const savings = billingCycle === 'yearly' && plan.priceYearly
    ? Math.round(((plan.priceMonthly * 12 - plan.priceYearly) / (plan.priceMonthly * 12)) * 100)
    : 0;

  const featureList = Object.entries(plan.features)
    .filter(([_, included]) => included)
    .map(([feature]) => FEATURE_LABELS[feature] || feature);

  const isPopular = plan.key === 'growth';
  const isEnterprise = plan.key === 'enterprise';

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between">
          <Text variant="headingMd" as="h3">{plan.name}</Text>
          <InlineStack gap="200">
            {isPopular && <Badge tone="info">Popular</Badge>}
            {isCurrent && <Badge tone="success">Current Plan</Badge>}
          </InlineStack>
        </InlineStack>

        {plan.description && (
          <Text as="span" variant="bodySm" tone="subdued">{plan.description}</Text>
        )}

        <Divider />

        <BlockStack gap="200">
          <InlineStack gap="100" align="start" blockAlign="baseline">
            <Text variant="heading2xl" as="span">{priceFormatted}</Text>
            <Text as="span" variant="bodySm" tone="subdued">
              /{billingCycle === 'yearly' ? 'year' : 'month'}
            </Text>
          </InlineStack>

          {monthlyEquivalent && (
            <Text as="span" variant="bodySm" tone="subdued">
              ({monthlyEquivalent}/mo billed annually)
            </Text>
          )}

          {savings > 0 && (
            <Badge tone="success">{`Save ${savings}% yearly`}</Badge>
          )}
        </BlockStack>

        <Divider />

        <BlockStack gap="200">
          <Text variant="headingSm" as="h4">Order Limit</Text>
          <Text as="span" variant="bodyLg" fontWeight="semibold">
            {plan.orderLimitFormatted} orders/month
          </Text>
        </BlockStack>

        <Divider />

        <BlockStack gap="200">
          <Text variant="headingSm" as="h4">Features</Text>
          <List type="bullet">
            {featureList.map((feature) => (
              <List.Item key={feature}>{feature}</List.Item>
            ))}
          </List>
        </BlockStack>

        <Box paddingBlockStart="400">
          {isCurrent ? (
            <Button disabled fullWidth>
              Current Plan
            </Button>
          ) : isEnterprise ? (
            <Button
              variant="secondary"
              fullWidth
              onClick={() => window.open('mailto:suvojit@edesy.in', '_blank')}
            >
              Contact Sales
            </Button>
          ) : (
            <Button
              variant="primary"
              fullWidth
              loading={isLoading}
              onClick={() => onSelect(plan.id)}
            >
              {currentPlanKey ? 'Switch to this plan' : 'Get Started'}
            </Button>
          )}
        </Box>
      </BlockStack>
    </Card>
  );
};

export default PricingCard;
